import api from './axiosInstance';
import { TestExecutionData } from '../components/TestAutomation/TestExecution/const/TestExecution';

// API Response Types
export interface TestExecutionSummaryResponse {
  test_execution_id: string;
  test_collection_name: string;
  workflow_id: string;
  product_version: string;
  product_number: string;
  start_timestamp: string;
  duration: string;
  statistics: {
    total_executed: number;
    success_count: number;
    failed_count: number;
    skipped_count: number;
    success_rate: number;
    failed_rate: number;
    skipped_rate: number;
  };
}

export interface TestCaseResponse {
  test_case_id: string;
  test_case_name: string;
  details: string; // JSON string, needs parsing
  time_taken: string; // seconds as string, e.g. "13.527556"
  result: 'Success' | 'Failure' | 'Warning' | 'Running' | 'Skipped';
  log_url?: string;
}

export interface TestCasePaginatedResponse {
  data: TestCaseResponse[];
  total: number;
  limit: number;
  offset: number;
  nextPage: number; // next offset, 0 if no more pages
}

// Request Parameter Types
export type TestCaseSortBy = 'createdAt' | 'updatedAt' | 'testCaseId' | 'testCaseName' | 'timeTaken' | 'result';
export type TestCaseSortOrder = 'ASC' | 'DESC';

export interface GetTestCasesParams {
  limit?: number; // default: 20
  offset?: number; // default: 0
  sortBy?: TestCaseSortBy; // default: "createdAt"
  sortOrder?: TestCaseSortOrder; // default: "ASC"
  // Filter parameters
  result?: string; // Comma-separated values, e.g. "Success,Failure,Skipped"
  test_case_name?: string; // Partial match filter
  details?: string; // JSON string search filter
  time_taken?: string; // Seconds format, e.g. "13"
}

// Paginated Response Type
type PaginatedResponse = {
  data: TestExecutionData[];
  total: number;
  limit: number;
  offset: number;
  nextPage: string | null;
};

export const testExecutionService = {
  /**
   * Get test executions list with pagination, filtering and sorting
   * GET /api/test-executions
   */
  async getTestExecutions(
    page: number = 1,
    limit: number = 20,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    filters: any = {},
    sortBy: string = 'createdAt',
    sortOrder: 'ASC' | 'DESC' = 'DESC'
  ): Promise<PaginatedResponse> {
    const params = { page, limit, sortBy, sortOrder, ...filters };
    const response = await api.get('api/test-executions', { params });
    return response.data;
  },

  /**
   * Create a new test execution
   * POST /api/test-executions
   */
  async createTestExecution(params: { testCollectionName: string; workflowId: string; username?: string }) {
    const response = await api.post('api/test-executions', params);
    return response.data;
  },

  /**
   * Get test execution summary
   * GET /api/test-executions/:testExecutionId/summary
   */
  async getTestExecutionSummary(testExecutionId: string): Promise<TestExecutionSummaryResponse> {
    const response = await api.get(`/api/test-executions/${testExecutionId}/summary`);
    return response.data;
  },

  /**
   * Get test cases with pagination and sorting
   * GET /api/test-executions/:testExecutionId/test-cases
   */
  async getTestCases(testExecutionId: string, params?: GetTestCasesParams): Promise<TestCasePaginatedResponse> {
    const queryParams = new URLSearchParams();

    if (params?.limit !== undefined) {
      queryParams.append('limit', params.limit.toString());
    }
    if (params?.offset !== undefined) {
      queryParams.append('offset', params.offset.toString());
    }
    if (params?.sortBy) {
      queryParams.append('sortBy', params.sortBy);
    }
    if (params?.sortOrder) {
      queryParams.append('sortOrder', params.sortOrder);
    }
    // Filter parameters
    if (params?.result) {
      queryParams.append('result', params.result);
    }
    if (params?.test_case_name) {
      queryParams.append('test_case_name', params.test_case_name);
    }
    if (params?.details) {
      queryParams.append('details', params.details);
    }
    if (params?.time_taken) {
      queryParams.append('time_taken', params.time_taken);
    }

    const queryString = queryParams.toString();
    const url = `/api/test-executions/${testExecutionId}/test-cases${queryString ? `?${queryString}` : ''}`;

    const response = await api.get(url);
    return response.data;
  }
};

// Default export for backward compatibility with HEAD branch imports
export default testExecutionService;
